import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import FuelPurchaseReportService from "../../services/FuelPurchaseReportService";
import ProjectService from "../../services/ProjectService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import VendorService from "../../services/VendorServices";
import utility from "../../utils/utility";
import ReactPaginate from "react-paginate";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import XLSX from "xlsx-js-style";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function FuelPurchaseReport() {
  const [reports, setReports] = useState([]);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(false);

  const [projectCategories, setProjectCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedProjectCategoryId, setSelectedProjectCategoryId] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatInputDate(startDate),
      endDate: formatInputDate(endDate),
    };
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";

    const projectName = project.projectName || project.name || "";
    const categoryName = project.projectCategoryName || project.categoryName || project.category || "";
    const projectCode = project.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const loadProjects = async () => {
    try {
      const companyId = StorageService.activeCompany().companyId;
      const res = await ProjectService.getActive(companyId);
      const data = res?.data || res || [];
      const mapped = data.map(p => ({ id: p.id || p.projectId, projectName: p.projectName, projectCode: p.projectCode, projectCategoryId: p.projectCategoryId || p.categoryId }));
      setProjects(mapped);
    } catch {
      setProjects([]);
    }
  };

  const loadProjectCategories = async () => {
    try {
      const res = await ProjectCategoryService.getAll();
      setProjectCategories(res?.data || res || []);
    } catch {
      setProjectCategories([]);
    }
  };

  const loadVendors = async () => {
    try {
      const companyId = StorageService.activeCompany().companyId;
      const res = await VendorService.getByCompanyId(companyId);
      setVendors(res?.data || res || []);
    } catch {
      setVendors([]);
    }
  };

  const loadSuppliers = async (projectCategoryId, projectId) => {
    try {
      const res = await FuelPurchaseReportService.getSuppliers({
        projectCategoryId: projectCategoryId || null,
        project: projectId || null
      });
      setFilteredVendors(res || []);
    } catch {
      setFilteredVendors([]);
    }
  };

  const displayedVendors = (selectedProjectCategoryId || selectedProjectId) ? filteredVendors : vendors;

  useEffect(() => {
    loadProjectCategories();
    loadProjects();
    loadVendors();
    loadSuppliers();

    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setFromDate(monthStart);
    setToDate(monthEnd);
    handleSearch(1, { fromDate: monthStart, toDate: monthEnd });
  }, []);

  useEffect(() => {
    loadSuppliers(selectedProjectCategoryId, selectedProjectId);
    if (selectedVendorId && !displayedVendors.some(v => String(v.id) === String(selectedVendorId))) {
      setSelectedVendorId("");
    }
  }, [selectedProjectCategoryId, selectedProjectId, displayedVendors, selectedVendorId]);

  const handleSearch = async (pageNumber = currentPage, filterOverrides = {}) => {
    setLoading(true);
    try {
      setReports([]);
      const companyId = StorageService.activeCompany().companyId;
      const filters = {
        projectCategoryId: selectedProjectCategoryId || null,
        project: selectedProjectId || null,
        vendorName: selectedVendorId || null,
        fromDate: fromDate || null,
        toDate: toDate || null,
        invoiceNo: invoiceNo || null,
        ...filterOverrides
      };

      const res = await FuelPurchaseReportService.getReport({
        pageNumber,
        noofRows: itemsPerPage,
        companyId,
        ...filters
      });

      setReports(res.data || []);
      setPageCount(res.pagination?.totalPages || 1);
      setCurrentPage(pageNumber);

    } catch (error) {
      console.error(error);
      showMessage("Error fetching fuel purchase report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    handleSearch(newPage);
  };

  const handleSearchClick = () => {
    handleSearch(1);
  };

  const handleClearFilters = () => {
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    const clearedFilters = {
      projectCategoryId: null,
      project: null,
      vendorName: null,
      fromDate: monthStart,
      toDate: monthEnd,
      invoiceNo: null
    };

    setSelectedProjectCategoryId("");
    setSelectedProjectId("");
    setSelectedVendorId("");
    setFromDate(monthStart);
    setToDate(monthEnd);
    setInvoiceNo("");
    handleSearch(1, clearedFilters);
  };

  const validateExportFilters = () => {
    return true;
  };

  const handleExportExcel = async () => {
    if (!validateExportFilters()) return;

    try {
      const rows = await FuelPurchaseReportService.exportExcel({
        projectCategoryId: selectedProjectCategoryId || null,
        project: selectedProjectId || null,
        vendorName: selectedVendorId || null,
        fromDate: fromDate || null,
        toDate: toDate || null,
        invoiceNo: invoiceNo || null
      });

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      const thin = { style: "thin" };
      const cellBorder = { top: thin, bottom: thin, left: thin, right: thin };
      const center = { horizontal: "center", vertical: "center", wrapText: true };
      const labelStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: "left", vertical: "center" },
        border: cellBorder
      };
      const valueStyle = {
        font: { sz: 10 },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: cellBorder
      };
      const numStyle = {
        font: { sz: 10 },
        alignment: { horizontal: "right", vertical: "center" },
        border: cellBorder
      };
      const tableHeaderStyle = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border: cellBorder,
        fill: { fgColor: { rgb: "FFD9D9D9" } }
      };
      const totalLabelStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: "right", vertical: "center" },
        border: cellBorder
      };
      const totalValueStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: "right", vertical: "center" },
        border: cellBorder
      };

      const styleCells = (r, colCount, styleForCol) => {
        for (let c = 0; c < colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (!ws[cell]) ws[cell] = { v: "", t: "s" };
          ws[cell].s = styleForCol(c);
        }
      };

      const addDataRow = (rowData, styleForCol) => {
        const r = row;
        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r, c: 0 } });
        styleCells(r, rowData.length, styleForCol);
        row++;
      };

      let row = 0;
      const companyName = StorageService.activeCompany()?.companyName || "";
      const projectName = formatProjectDisplay(projects.find((p) => p.id == selectedProjectId) || { projectName: selectedProjectId });
      const categoryName = projectCategories.find((c) => String(c.id || c.projectCategoryId) === String(selectedProjectCategoryId))?.categoryName || "-";
      const supplierName = vendors.find((v) => String(v.id) === String(selectedVendorId))?.vendorName || "-";

      XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: row, c: 0 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "left", vertical: "center" }
      };
      row++;

      XLSX.utils.sheet_add_aoa(ws, [["Fuel Purchase Report"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 7 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center", vertical: "center" }
      };
      row += 2;

      const filterRows = [
        ["Project Category:", categoryName, "", "Project:", projectName],
        ["Supplier:", supplierName, "", "Start Date:", utility.formatDateDDMMMYYYY(fromDate)],
        ["End Date:", utility.formatDateDDMMMYYYY(toDate), "", "", ""]
      ];

      filterRows.forEach((filterRow) => {
        XLSX.utils.sheet_add_aoa(ws, [filterRow], { origin: { r: row, c: 0 } });
        styleCells(row, filterRow.length, (c) => (c === 0 || c === 3 ? labelStyle : valueStyle));
        row++;
      });
      row++;

      const detailHeaders = [
        "Date",
        "Supplier Name",
        "Invoice No#",
        "Item",
        "Qty(Ltrs)",
        "Rate",
        "Amount",
        "Received By"
      ];
      const headerRowIndex = row;
      addDataRow(detailHeaders, () => tableHeaderStyle);

      rows.forEach((r) => {
        const qtyVal = Number(r.Qty || r.qty || 0);
        const rateVal = Number(r.Rate || r.rate || 0);
        const amtVal = Number(r.Amount || r.amount || 0);
        const dateSerial = utility.convertDateToExcelSerial(r.PurchaseDate || r.purchaseDate);
        const startR = row;
        addDataRow(
          [
            dateSerial || "-",
            r.VendorName || r.vendorName || "-",
            r.VoucherNo || r.voucherNo || "-",
            r.FuelType || r.fuelType || "-",
            qtyVal,
            rateVal,
            amtVal,
            r.CreatedBy || r.createdBy || "-"
          ],
          (c) => (c >= 4 && c <= 6 ? numStyle : valueStyle)
        );
        const cellD = XLSX.utils.encode_cell({ r: startR, c: 0 });
        if (ws[cellD] && dateSerial) { ws[cellD].t = "n"; ws[cellD].v = dateSerial; ws[cellD].z = "dd-mm-yyyy"; }
        const cellQ = XLSX.utils.encode_cell({ r: startR, c: 4 });
        if (ws[cellQ]) { ws[cellQ].t = "n"; ws[cellQ].v = qtyVal; ws[cellQ].z = "#,##0.00"; }
        const cellR = XLSX.utils.encode_cell({ r: startR, c: 5 });
        if (ws[cellR]) { ws[cellR].t = "n"; ws[cellR].v = rateVal; ws[cellR].z = "#,##0.00"; }
        const cellA = XLSX.utils.encode_cell({ r: startR, c: 6 });
        if (ws[cellA]) { ws[cellA].t = "n"; ws[cellA].v = amtVal; ws[cellA].z = "#,##0.00"; }
      });

      const totalQty = rows.reduce((sum, r) => sum + Number(r.Qty || r.qty || 0), 0);
      const totalAmount = rows.reduce((sum, r) => sum + Number(r.Amount || r.amount || 0), 0);
      const totR = row;
      addDataRow(
        ["", "", "", "Total", totalQty, "", totalAmount, ""],
        (c) => {
          if (c === 3) return totalLabelStyle;
          if (c === 4 || c === 6) return totalValueStyle;
          return valueStyle;
        }
      );
      const cellTQ = XLSX.utils.encode_cell({ r: totR, c: 4 });
      if (ws[cellTQ]) { ws[cellTQ].t = "n"; ws[cellTQ].v = totalQty; ws[cellTQ].z = "#,##0.00"; }
      const cellTA = XLSX.utils.encode_cell({ r: totR, c: 6 });
      if (ws[cellTA]) { ws[cellTA].t = "n"; ws[cellTA].v = totalAmount; ws[cellTA].z = "#,##0.00"; }

      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: headerRowIndex, c: 0 },
          e: { r: totR, c: detailHeaders.length - 1 }
        })
      };

      ws["!cols"] = [
        { wch: 12 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 12 },
        { wch: 14 },
        { wch: 18 }
      ];

      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row - 1, c: 7 } });
      XLSX.utils.book_append_sheet(wb, ws, "FuelPurchase");
      XLSX.writeFile(wb, `Fuel_Purchase_Report_${utility.formatDateDDMMMYYYY(new Date())}.xlsx`);
    } catch (err) {
      console.error(err);
      showMessage("Error exporting Excel", "error");
    }
  };

  const handleExportPDF = async () => {
    if (!validateExportFilters()) return;

    setLoading(true);
    try {
      const rows = await FuelPurchaseReportService.exportExcel({
        projectCategoryId: selectedProjectCategoryId || null,
        project: selectedProjectId || null,
        vendorName: selectedVendorId || null,
        fromDate: fromDate || null,
        toDate: toDate || null,
        invoiceNo: invoiceNo || null
      });

      const normalizedData = rows.map(r => ({
        purchaseDate: r.PurchaseDate || r.purchaseDate,
        fuelType: r.FuelType || r.fuelType,
        qty: r.Qty || r.qty,
        rate: r.Rate || r.rate,
        amount: r.Amount || r.amount,
        voucherNo: r.VoucherNo || r.voucherNo,
        projectName: r.ProjectName || r.projectName,
        projectCategory: r.ProjectCategory || r.projectCategory,
        vendorName: r.VendorName || r.vendorName,
        createdBy: r.CreatedBy || r.createdBy
      }));

      const doc = new jsPDF("p", "mm", "a4");
      const pdfLogo = await getPdfLogoDataUrl(logo);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageMargin = 14;
      const activeCompany = StorageService.activeCompany();
      const companyName = activeCompany?.companyName || "Devi Projects";

      const drawPageHeader = () => {
        const right = pageWidth - pageMargin;
        doc.setFont(undefined, "bold");
        doc.setFontSize(12);
        const companyNameWidth = doc.getTextWidth(companyName);

        const logoWidth = 5;
        const logoHeight = 10;
        const logoX = pageMargin + 6 + (companyNameWidth - logoWidth) / 2;
        const logoY = 8;

        doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
        doc.text(companyName, pageMargin + 6, 23);

        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

        const title = "Fuel Purchase Report";
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text(title, pageWidth / 2, 35, { align: "center" });

        const titleWidth = doc.getTextWidth(title);
        doc.setLineWidth(0.3);
        doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
      };

      drawPageHeader();

      const details = [
        ["Project Category:", projectCategories.find((c) => String(c.id || c.projectCategoryId) === String(selectedProjectCategoryId))?.categoryName || "-"],
        ["Project:", formatProjectDisplay(projects.find((p) => String(p.id) === String(selectedProjectId)) || { projectName: selectedProjectId })],
        ["Supplier:", vendors.find((v) => String(v.id) === String(selectedVendorId))?.vendorName || "-"],
        ["Start Date:", utility.formatDateDMY(fromDate)],
        ["End Date:", utility.formatDateDMY(toDate)]
      ];

      let yPos = 52;
      const labelX = pageMargin;
      const valueX = pageMargin + 40;
      doc.setFontSize(10);

      details.forEach(([label, value], index) => {
        doc.setFont(undefined, "bold");
        doc.text(label, labelX, yPos + index * 6);
        doc.setFont(undefined, "normal");
        doc.text(String(value || "-"), valueX, yPos + index * 6);
      });

      yPos += 34;

      const buildSummaryRows = () => {
        const summaryMap = {};
        const fuelTypes = ["DIESEL", "PETROL", "GAS"];

        normalizedData.forEach((r) => {
          const supplierName = r.vendorName || "-";
          const fuelType = (r.fuelType || "").toUpperCase();
          const qty = Number(r.qty || 0);
          const amount = Number(r.amount || 0);

          if (!summaryMap[supplierName]) {
            summaryMap[supplierName] = {
              DIESEL: { qty: 0, amount: 0 },
              PETROL: { qty: 0, amount: 0 },
              GAS: { qty: 0, amount: 0 }
            };
          }

          if (fuelTypes.includes(fuelType)) {
            summaryMap[supplierName][fuelType].qty += qty;
            summaryMap[supplierName][fuelType].amount += amount;
          }
        });

        const rows = Object.entries(summaryMap).map(([supplier, totals]) => [
          supplier,
          totals.DIESEL.qty.toFixed(2),
          totals.DIESEL.amount.toFixed(2),
          totals.PETROL.qty.toFixed(2),
          totals.PETROL.amount.toFixed(2),
          totals.GAS.qty.toFixed(2),
          totals.GAS.amount.toFixed(2)
        ]);

        if (rows.length === 0) {
          rows.push(["-", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00"]);
        }

        const totals = rows.reduce(
          (acc, row) => {
            acc.dieselQty += Number(row[1]);
            acc.dieselAmt += Number(row[2]);
            acc.petrolQty += Number(row[3]);
            acc.petrolAmt += Number(row[4]);
            acc.gasQty += Number(row[5]);
            acc.gasAmt += Number(row[6]);
            return acc;
          },
          { dieselQty: 0, dieselAmt: 0, petrolQty: 0, petrolAmt: 0, gasQty: 0, gasAmt: 0 }
        );

        rows.push([
          "Total",
          totals.dieselQty.toFixed(2),
          totals.dieselAmt.toFixed(2),
          totals.petrolQty.toFixed(2),
          totals.petrolAmt.toFixed(2),
          totals.gasQty.toFixed(2),
          totals.gasAmt.toFixed(2)
        ]);

        return rows;
      };

      const summaryRows = buildSummaryRows();

      autoTable(doc, {
        startY: yPos,
        margin: { top: 48, left: 14, right: 14 },
        head: [
          [
            { content: "Supplier Name", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
            { content: "Diesel", colSpan: 2, styles: { halign: "center" } },
            { content: "Petrol", colSpan: 2, styles: { halign: "center" } },
            { content: "Gas", colSpan: 2, styles: { halign: "center" } }
          ],
          [
            "Litre(s)",
            "Amount",
            "Litre(s)",
            "Amount",
            "Litre(s)",
            "Amount"
          ]
        ],
        body: summaryRows,
        theme: "grid",
        styles: {
          fontSize: 8,
          lineColor: [180, 180, 180],
          lineWidth: 0.3
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          lineColor: [180, 180, 180],
          lineWidth: 0.3
        },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" }
        },
        didParseCell: (data) => {
          if (data.row.section === "body" && data.row.index === data.table.body.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 240, 240];
          }
        }
      });

      const summaryEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 10;
      const tableHead = [["Date", "Supplier Name", "Invoice No#", "Item", "Qty(Ltrs)", "Rate", "Amount", "Received By"]];
      const tableBody = normalizedData.map((r) => [
        utility.formatDateDDMMMYYYY(r.purchaseDate),
        r.vendorName || "-",
        r.voucherNo || "-",
        r.fuelType || "-",
        Number(r.qty || 0).toFixed(2),
        Number(r.rate || 0).toFixed(2),
        Number(r.amount || 0).toFixed(2),
        r.createdBy || "-"
      ]);

      const totalQty = normalizedData.reduce((sum, r) => sum + Number(r.qty || 0), 0);
      const totalAmount = normalizedData.reduce((sum, r) => sum + Number(r.amount || 0), 0);

      autoTable(doc, {
        startY: summaryEndY,
        margin: { top: 48, left: 14, right: 14 },
        head: tableHead,
        body: [
          ...tableBody,
          ["", "", "", "Total", totalQty.toFixed(2), "", totalAmount.toFixed(2), ""]
        ],
        theme: "grid",
        styles: {
          fontSize: 8,
          lineColor: [180, 180, 180],
          lineWidth: 0.3
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          lineColor: [180, 180, 180],
          lineWidth: 0.3
        },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" },
          7: { halign: "left" }
        },
        didParseCell: (data) => {
          if (data.row.section === "body" && data.row.index === data.table.body.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 240, 240];
          }
        }
      });

      const drawPageNumber = (pageNumber, totalPages) => {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 7, { align: "center" });
      };

      const pageCount = doc.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setPage(1);
      drawPageNumber(1, pageCount);
      for (let i = 2; i <= pageCount; i++) {
        doc.setPage(i);
        drawPageHeader();
        drawPageNumber(i, pageCount);
      }

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        if (i === pageCount) {
          doc.setFontSize(10);
          doc.text("Prepared By", 20, pageHeight - 20);
          doc.text("Checked By", pageWidth / 2, pageHeight - 20, { align: "center" });
          doc.text("Approved By", pageWidth - 20, pageHeight - 20, { align: "right" });
        }
      }

      doc.save(`Fuel_Purchase_Report_${utility.formatDateDDMMMYYYY(new Date())}.pdf`);
    } catch (err) {
      console.error(err);
      showMessage("Error exporting PDF", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageTitle="Fuel Purchase Report" secondTitle="Fuel Management" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="row">
        <div className="card py-3 bg-white shadow-sm">

          <div className="row mb-3 px-3">
            <div className="col-md-3 mb-2">
              <label className="form-label">Project Category</label>
              <select
                className="form-select"
                value={selectedProjectCategoryId}
                onChange={e => { setSelectedProjectCategoryId(e.target.value); setSelectedProjectId(""); }}
              >
                <option value="">Select Category</option>
                {projectCategories.map(c => (
                  <option key={c.id || c.projectCategoryId} value={c.id || c.projectCategoryId}>
                    {c.categoryName || c.projectCategoryName || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-2">
              <label className="form-label">Project</label>
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
              >
                <option value="">Select Project</option>
                {(selectedProjectCategoryId ? projects.filter(p => String(p.projectCategoryId || p.categoryId) === String(selectedProjectCategoryId)) : projects).map(p => (
                  <option key={p.id} value={p.id}>
                    {formatProjectDisplay(p)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-2">
              <label className="form-label">Fuel Supplier</label>
              <select
                className="form-select"
                value={selectedVendorId}
                onChange={e => setSelectedVendorId(e.target.value)}
              >
                <option value="">Select Supplier</option>
                {(selectedProjectCategoryId || selectedProjectId ? filteredVendors : vendors).map(v => (
                  <option key={v.id} value={v.id}>
                    {v.vendorName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-2">
              <label className="form-label">Invoice No#</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search Invoice No#"
                value={invoiceNo}
                onChange={e => setInvoiceNo(e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-2">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-2">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mb-3 d-flex justify-content-end align-items-center">
            <div className="d-flex gap-2">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                <i className="ti ti-refresh me-1"></i>
                Clear
              </button>
              <button className="btn btn-primary" onClick={handleSearchClick} disabled={loading}>
                <i className="ti ti-search me-1"></i>
                {loading ? "Searching..." : "View Report"}
              </button>
            </div>
          </div>
          <hr></hr>
          <div className="mb-3 d-flex justify-content-end align-items-center">
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={handleExportPDF}>
                Export PDF
              </button>
              <button className="btn btn-success" onClick={handleExportExcel}>
                Export Excel
              </button>
            </div>

          </div>

          <div className="px-3 mb-4">
            <h6 className="mb-2"><strong>Fuel Purchase Details</strong></h6>
            <div className="table-responsive">
              <table className="table table-striped table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Supplier Name</th>
                    <th>Invoice No#</th>
                    <th>Item</th>
                    <th className="text-end">Qty(Ltrs)</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Amount</th>
                    <th>Received By</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="spinner-border text-primary"></div>
                      </td>
                    </tr>
                  ) : reports.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">
                        No fuel purchase data found
                      </td>
                    </tr>
                  ) : (
                    reports.map((r, index) => (
                      <tr key={r.id || index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{utility.formatDateDDMMMYYYY(r.purchaseDate)}</td>
                        <td>{r.vendorName || "-"}</td>
                        <td>{r.voucherNo || "-"}</td>
                        <td>{r.fuelType || "-"}</td>
                        <td className="text-end">{Number(r.qty || 0).toFixed(2)}</td>
                        <td className="text-end">{Number(r.rate || 0).toFixed(2)}</td>
                        <td className="text-end">{Number(r.amount || 0).toFixed(2)}</td>
                        <td>{r.createdBy || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-flex justify-content-end px-3">
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              previousLabel="< Previous"
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              onPageChange={handlePageClick}
              pageCount={pageCount}
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              forcePage={currentPage - 1}
            />
          </div>

        </div>
      </div>
    </>
  );
}
